import { useEffect, useState } from 'react';
import i18next from 'i18next';
import { getI18nInitPromise } from '~/config/i18n/internationalization';

const INIT_TIMEOUT_MS = 10000;

/**
 * Tracks whether i18next has finished loading its bundled namespaces.
 *
 * Uses plain state rather than Suspense: react-redux 9 renders store updates at
 * sync priority, and suspending during such a render throws React error #426.
 *
 * Opens on failure or after a timeout, never blocking the app indefinitely.
 * i18next resolves lookups through the fallback language, so a locale that fails
 * to load renders English.
 *
 * @returns `true` once translations can be resolved.
 */
const useI18nReady = (): boolean => {
  const [ready, setReady] = useState(() => i18next.isInitialized);

  useEffect(() => {
    if (ready) {
      return undefined;
    }
    let cancelled = false;
    const open = () => {
      if (!cancelled) {
        setReady(true);
      }
    };
    const timeout = setTimeout(open, INIT_TIMEOUT_MS);
    void getI18nInitPromise().then(open, open);
    return () => {
      cancelled = true;
      clearTimeout(timeout);
    };
  }, [ready]);

  return ready;
};

export { useI18nReady };
